import { useResponsive } from '@bitrise/bitkit-v2';
import { useMemo } from 'react';

import useQueryParams from '@/hooks/useQueryParams';
import useTableSort from '@/hooks/useTableSort';
import { BuildCacheToolIds } from '@/models/BuildCacheTool';
import { BuildToolInvocationWithDetails } from '@/models/BuildToolInvocation';
import InvocationListMobile from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/InvocationListMobile';
import InvocationListSkeleton from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/InvocationListSkeleton';
import { InvocationListTableContextProvider } from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/InvocationListTableContext';
import InvocationListTableDesktop from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/components/InvocationList/InvocationListTableDesktop';
import {
  DEFAULT_ITEMS_PER_PAGE,
  MAX_ITEMS_PER_PAGE,
} from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/constants';
import {
  parseSortBy,
  SortByQueryParamValues,
  SortDirectionQueryParamValues,
} from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/enums/invocationListTable';
import {
  InvocationListResponse,
  PageQueryParamKeys,
} from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/hooks/useInvocations';

type InvocationListProps = {
  isBuildSpecific: boolean;
  isProjectSpecific?: boolean;
  isFiltered: boolean;
  isInSelectMode?: boolean;
  invocationsToCompare?: BuildToolInvocationWithDetails[];
  setInvocationsToCompare?: (value: BuildToolInvocationWithDetails[]) => void;
  invocationsResponseData?: InvocationListResponse;
  setPage?: (page: number) => void;
  setPerPage?: (itemsPerPage: number) => void;
  selectedTool: BuildCacheToolIds;
};

const InvocationList = ({
  isBuildSpecific,
  isProjectSpecific = false,
  isFiltered,
  isInSelectMode = false,
  invocationsToCompare = [],
  setInvocationsToCompare,
  invocationsResponseData,
  setPage,
  setPerPage,
  selectedTool,
}: InvocationListProps) => {
  const { isMobile } = useResponsive();

  const { registerColumn, onSortClear } = useTableSort<string>({
    historyParams: { sortBy: PageQueryParamKeys.sortBy, sortDirection: PageQueryParamKeys.sortDirection },
  });

  const queryParams = useQueryParams();
  // Sanitised the same way as in the request (see parseSortBy), so the mobile sort select doesn't sit on a value
  // it has no option for while the list comes back in the default order.
  const sortBy = useMemo(() => parseSortBy(queryParams.get(PageQueryParamKeys.sortBy)), [queryParams]);
  const sortDirection = useMemo(
    () => (sortBy ? (queryParams.get(PageQueryParamKeys.sortDirection) as SortDirectionQueryParamValues | null) : null),
    [queryParams, sortBy],
  );
  const itemsPerPage = useMemo(() => {
    // parseInt mirrors the String#to_i the controller parses the param with, so the skeleton reserves
    // as many rows as the API will actually serve.
    const perPage = parseInt(queryParams.get(PageQueryParamKeys.perPage) ?? '', 10);
    return perPage >= 1 ? Math.min(perPage, MAX_ITEMS_PER_PAGE) : DEFAULT_ITEMS_PER_PAGE;
  }, [queryParams]);

  const { onSortClick: onStartedAtSortClick, sortedBy: startedAtSortDirection } = registerColumn(
    SortByQueryParamValues.startedAt,
  );
  const { onSortClick: onDurationSortClick, sortedBy: durationSortDirection } = registerColumn(
    SortByQueryParamValues.duration,
  );

  if (!invocationsResponseData || !setPage || !setPerPage) {
    // Reserving a row per incoming item keeps the document from shrinking while the list reloads, which
    // would otherwise clamp the scroll position upwards.
    return (
      <InvocationListSkeleton
        isBuildSpecific={isBuildSpecific}
        isProjectSpecific={isProjectSpecific}
        rows={itemsPerPage}
        selectedTool={selectedTool}
      />
    );
  }

  const { items, paging } = invocationsResponseData;

  const contextProps = {
    isBuildSpecific,
    isProjectSpecific,
    isFiltered,
    isInSelectMode,
    invocationsToCompare,
    setInvocationsToCompare,
    page: paging.page,
    setPage,
    itemCount: paging.totalCount,
    itemsPerPage: paging.perPage,
    setItemsPerPage: setPerPage,
    selectedTool,
  };

  if (isMobile) {
    return (
      <InvocationListTableContextProvider {...contextProps}>
        <InvocationListMobile
          invocations={items}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onStartedAtSortClick={onStartedAtSortClick}
          onDurationSortClick={onDurationSortClick}
          onSortClear={onSortClear}
        />
      </InvocationListTableContextProvider>
    );
  }

  return (
    <InvocationListTableContextProvider {...contextProps}>
      <InvocationListTableDesktop
        invocations={items}
        startedAtSortDirection={startedAtSortDirection}
        onStartedAtSortClick={onStartedAtSortClick}
        durationSortDirection={durationSortDirection}
        onDurationSortClick={onDurationSortClick}
      />
    </InvocationListTableContextProvider>
  );
};

export default InvocationList;
